import { Assets, Container } from 'pixi.js';
import { PROTOCOL_VERSION, type ServerMessage } from '@nanocars/shared';
import { LOGICAL_WIDTH, type Scene, type SceneManager } from './SceneManager';
import { UI_PATHS } from '../assets/manifest';
import { COLORS } from '../ui/theme';
import { makeImageButton, makeLabel, makeTextInput, type TextInputHandle } from '../ui/widgets';
import { FocusRing } from '../ui/focus';
import { session } from '../state/Session';
import { TitleScene } from './TitleScene';
import { LobbyScene } from './LobbyScene';

const CODE_LENGTH = 4;

function sanitizeCode(raw: string): string {
  return raw
    .toUpperCase()
    .replace(/[^A-Z]/g, '')
    .slice(0, CODE_LENGTH);
}

// Room-code entry: a 4-letter code, Join and Back. Inline red text shows
// `badCode`/`full` (and any other server error) from the last join attempt.
export class JoinScene implements Scene {
  readonly name = 'join';
  readonly container = new Container();

  private codeInput: TextInputHandle | null = null;
  private joinBtn: Container | null = null;
  private backBtn: Container | null = null;
  private errorLabel: ReturnType<typeof makeLabel> | null = null;
  private focusRing: FocusRing | null = null;
  private unsubscribers: Array<() => void> = [];

  constructor(private readonly scenes: SceneManager) {}

  async enter(): Promise<void> {
    const buttonTexture = await Assets.load(UI_PATHS.button);

    const heading = makeLabel('Join Room', { fontSize: 32, fontWeight: '900', fill: COLORS.textLight });
    heading.anchor.set(0.5, 0);
    heading.x = LOGICAL_WIDTH / 2;
    heading.y = 120;
    this.container.addChild(heading);

    const codeLabel = makeLabel('Room code', { fontSize: 18, fill: COLORS.textLight });
    codeLabel.anchor.set(0.5, 0);
    codeLabel.x = LOGICAL_WIDTH / 2;
    codeLabel.y = 260;
    this.container.addChild(codeLabel);

    this.codeInput = makeTextInput('ABCD', CODE_LENGTH, { filter: sanitizeCode });
    this.codeInput.container.x = LOGICAL_WIDTH / 2 - 160;
    this.codeInput.container.y = 288;
    this.container.addChild(this.codeInput.container);

    this.joinBtn = makeImageButton(buttonTexture, 'Join', () => this.handleJoin());
    this.joinBtn.x = LOGICAL_WIDTH / 2 - 110;
    this.joinBtn.y = 366;
    this.container.addChild(this.joinBtn);

    this.backBtn = makeImageButton(buttonTexture, 'Back', () => this.handleBack());
    this.backBtn.x = LOGICAL_WIDTH / 2 - 110;
    this.backBtn.y = 460;
    this.container.addChild(this.backBtn);

    this.errorLabel = makeLabel('', { fontSize: 16, fill: 0xff6b5b });
    this.errorLabel.anchor.set(0.5, 0);
    this.errorLabel.x = LOGICAL_WIDTH / 2;
    this.errorLabel.y = 552;
    this.container.addChild(this.errorLabel);

    this.focusRing = new FocusRing(this.container, session.input);
    this.focusRing.setItems([
      { container: this.codeInput.container, activate: () => this.codeInput?.focus() },
      { container: this.joinBtn, activate: () => this.handleJoin() },
      { container: this.backBtn, activate: () => this.handleBack() },
    ]);

    this.unsubscribers.push(
      session.net.on('welcome', (msg) => this.handleWelcome(msg)),
      session.net.on('error', (msg) => this.handleError(msg)),
      onMenuBack(() => this.handleBack()),
    );
  }

  exit(): void {
    for (const unsub of this.unsubscribers) unsub();
    this.unsubscribers = [];
    this.focusRing?.dispose();
    this.codeInput?.destroy();
    this.container.removeChildren();
  }

  update(_dt: number): void {
    // Static form; nothing to animate.
  }

  resize(_w: number, _h: number): void {
    // Fixed 1280x720 logical layout; nothing depends on resize.
  }

  private handleJoin(): void {
    if (!this.codeInput) return;
    const code = this.codeInput.value;
    if (code.length !== CODE_LENGTH) {
      if (this.errorLabel) this.errorLabel.text = 'Enter a 4-letter code';
      return;
    }
    if (!session.net.connected) {
      if (this.errorLabel) this.errorLabel.text = 'Not connected to server';
      return;
    }
    const existingSession = session.net.session;
    session.net.send({
      t: 'join',
      v: PROTOCOL_VERSION,
      code,
      name: session.name,
      ...(existingSession !== null ? { session: existingSession } : {}),
    });
  }

  private handleBack(): void {
    void this.scenes.go(new TitleScene(this.scenes));
  }

  private handleWelcome(msg: Extract<ServerMessage, { t: 'welcome' }>): void {
    session.playerId = msg.playerId;
    session.net.session = msg.session;
    session.lobby = msg.lobby;
    void this.scenes.go(new LobbyScene(this.scenes));
  }

  private handleError(msg: Extract<ServerMessage, { t: 'error' }>): void {
    if (!this.errorLabel) return;
    this.errorLabel.text =
      msg.code === 'badCode' ? 'No room with that code' : msg.code === 'full' ? 'That room is full' : msg.msg;
  }
}

function onMenuBack(fn: () => void): () => void {
  const listener = (e: Event): void => {
    if ((e as CustomEvent<string>).detail === 'back') fn();
  };
  session.input.menu.addEventListener('menu', listener);
  return () => session.input.menu.removeEventListener('menu', listener);
}
